import { useMemo, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { PhotoThumb } from '@/components/PhotoThumb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Search, Filter, ChevronsUpDown, Check, X, ArrowUp, ArrowDown, ArrowUpDown, Pencil, Loader2, Eye, CalendarDays, PawPrint } from 'lucide-react';
import { animalsApi } from '../api';
import { AnimalsListParams } from '../types';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { sheltersApi } from '@/features/shelters/api';
import { env } from '@/config/env';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adoptionApi } from '@/features/adoption/api';

type TabValue = 'available' | 'adopted';

const normalizeStatusText = (code?: string | number | null, label?: string | null) => {
  return `${code ?? ''} ${label ?? ''}`
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const isAdoptedStatus = (code?: string | number | null, label?: string | null) => {
  const norm = normalizeStatusText(code, label);
  return /adoptad|adotad/.test(norm);
};

type AnimalStatusType = 'available' | 'paused' | 'adopted' | 'in_process' | 'deceased' | 'other';

const statusTypeOf = (code?: string | number | null, label?: string | null): AnimalStatusType => {
  const norm = normalizeStatusText(code, label);
  if (/dispon|available/.test(norm)) return 'available';
  if (/pausad|paused|suspendid/.test(norm)) return 'paused';
  if (/falecid|fallecid|deceas|muert/.test(norm)) return 'deceased';
  if (isAdoptedStatus(code, label)) return 'adopted';
  if (/proc|process|proceso|in_adoption_process|adoption_process/.test(norm)) return 'in_process';
  return 'other';
};

export default function AnimalsListPage({ viewMode = 'available' }: { viewMode?: 'available' | 'adopted' }) {
  const queryClient = useQueryClient();
  const { isCustomer } = useAuth();
  const [params, setParams] = useState<AnimalsListParams>({
    page: 1,
    pageSize: 20,
  });
  const activeTab = viewMode;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState<'name'|'species'|'sex'|'size'|'breed'|'status'|'shelter'|'microchip'|'createdAt'>('createdAt');
  const [dir, setDir] = useState<'asc'|'desc'>('desc');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewAnimal, setViewAnimal] = useState<any | null>(null);
  const [viewApplication, setViewApplication] = useState<any | null>(null);
  const [viewFormResponse, setViewFormResponse] = useState<any | null>(null);
  const [viewFormTemplate, setViewFormTemplate] = useState<any | null>(null);
  const [viewTimeline, setViewTimeline] = useState<any[]>([]);
  const [filters, setFilters] = useState<{
    species_id?: number;
    sex_id?: number;
    size_id?: number;
    color_id?: number;
    breed_id?: number;
    temperament_id?: number;
    status_id?: number;
    shelter_id?: number;
  }>({});

  // Lookups snapshot
  const { data: snapshot } = useLookupsSnapshot();
  const species = snapshot?.species || [];
  const sexes = snapshot?.sexes || [];
  const sizes = snapshot?.sizes || [];
  const statuses = snapshot?.statuses || [];
  const colors = snapshot?.colors || [];
  const breedsAll = snapshot?.breeds || [];
  const temperaments = snapshot?.temperaments || [];
  const speciesOpts = useMemo(() => species.map((i:any)=>({id:i.id,label:toOptionLabel(i), raw:i})), [species]);
  const sexOpts = useMemo(() => sexes.map((i:any)=>({id:i.id,label:toOptionLabel(i), raw:i})), [sexes]);
  const sizeOpts = useMemo(() => sizes.map((i:any)=>({id:i.id,label:toOptionLabel(i), raw:i})), [sizes]);
  const statusOpts = useMemo(() => statuses.map((i:any)=>({id:i.id,label:toOptionLabel(i), raw:i})), [statuses]);
  const adoptedStatusIds = useMemo(() => (statuses || []).filter((i:any) => isAdoptedStatus(i?.code, i?.label)).map((i:any) => i.id), [statuses]);
  const adoptedStatusOpts = useMemo(() => statusOpts.filter((opt) => adoptedStatusIds.includes(opt.id)), [statusOpts, adoptedStatusIds]);
  const availableStatusOpts = useMemo(() => statusOpts.filter((opt) => !adoptedStatusIds.includes(opt.id)), [statusOpts, adoptedStatusIds]);
  const statusFilterOptions = useMemo(
    () => (activeTab === 'adopted' ? adoptedStatusOpts : availableStatusOpts),
    [activeTab, adoptedStatusOpts, availableStatusOpts]
  );
  const colorOpts = useMemo(() => colors.map((i:any)=>({id:i.id,label:toOptionLabel(i), raw:i})), [colors]);
  const temperamentOpts = useMemo(() => temperaments.map((i:any)=>({id:i.id,label:toOptionLabel(i), raw:i})), [temperaments]);
  const [selectedSpecies, setSelectedSpecies] = useState<number|undefined>();
  const breedOpts = useMemo(()=> {
    const mapped = breedsAll.map((i:any)=>({id:i.id,label:toOptionLabel(i), raw:i}));
    const filtered = selectedSpecies ? mapped.filter(o => (o.raw.species_id ?? o.raw.speciesId) === selectedSpecies) : mapped;
    const idx = filtered.findIndex((o:any) => String(o.raw?.code || '').toLowerCase() === 'mixed' || /criollo/i.test(String(o.raw?.label || o.label)));
    if (idx > 0) {
      const [item] = filtered.splice(idx, 1);
      filtered.unshift(item);
    }
    return filtered;
  }, [breedsAll, selectedSpecies]);
  const derivedStatusId = useMemo(() => {
    if (params.status_id) return params.status_id;
    if (activeTab === 'adopted' && adoptedStatusIds.length) return adoptedStatusIds[0];
    return undefined;
  }, [params.status_id, activeTab, adoptedStatusIds]);
  const excludeStatusIds = useMemo(() => {
    if (params.status_id) return undefined;
    if (activeTab === 'available' && adoptedStatusIds.length) return adoptedStatusIds;
    return undefined;
  }, [params.status_id, activeTab, adoptedStatusIds]);

  const { data, isLoading } = useQuery({
    queryKey: ['animals-search', params, sort, dir, debouncedQuery, activeTab, derivedStatusId, (excludeStatusIds || []).join(',')],
    queryFn: () => {
      const q: any = {
        page: params.page || 1,
        pageSize: params.pageSize || 20,
        sort,
        dir,
      };
      if (params.species_id) q.speciesId = params.species_id;
      if ((params as any).sex_id) q.sexId = (params as any).sex_id;
      if (params.size_id) q.sizeId = params.size_id;
      if (params.breed_id) q.breedId = params.breed_id;
      if ((params as any).temperament_id) q.temperamentId = (params as any).temperament_id;
      if (derivedStatusId) q.statusId = derivedStatusId;
      if (excludeStatusIds?.length) q.excludeStatusIds = excludeStatusIds;
      if (params.shelter_id) q.shelterId = params.shelter_id;
      if (debouncedQuery.trim()) {
        q.q = debouncedQuery.trim();
      }
      return animalsApi.search(q);
    },
  });

  // Shelters for filter
  const { data: sheltersData } = useQuery({
    queryKey: ['shelters-filter'],
    queryFn: () => sheltersApi.list({ page:1, pageSize: 500 }),
  });
  const shelterRows: any[] | undefined = Array.isArray(sheltersData) ? sheltersData as any[] : (sheltersData as any)?.data;
  const shelterOpts = (shelterRows||[]).map((s:any)=>({id:s.id,label:s.name}));

  const handleSearch = () => {
    setParams({ ...params, page: 1 });
    setDebouncedQuery(searchQuery);
  };
  // Debounce typing in search input
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!adoptedStatusIds.length) return;
    setFilters((prev) => {
      if (!prev.status_id) return prev;
      const isAdopted = adoptedStatusIds.includes(prev.status_id);
      if ((activeTab === 'available' && isAdopted) || (activeTab === 'adopted' && !isAdopted)) {
        return { ...prev, status_id: undefined };
      }
      return prev;
    });
    setParams((prev) => {
      if (!prev.status_id) return prev;
      const isAdopted = adoptedStatusIds.includes(prev.status_id);
      if ((activeTab === 'available' && isAdopted) || (activeTab === 'adopted' && !isAdopted)) {
        return { ...prev, status_id: undefined, page: 1 };
      }
      return prev;
    });
  }, [activeTab, adoptedStatusIds]);

  const applyFilters = () => {
    const next: AnimalsListParams = {
      ...params,
      page: 1,
      species_id: filters.species_id,
      status_id: filters.status_id,
      size_id: filters.size_id,
      breed_id: filters.breed_id,
      color_id: filters.color_id,
      temperament_id: filters.temperament_id,
      // sex filter not in AnimalsListParams; backend may accept, so include generically
      // @ts-ignore
      sex_id: filters.sex_id,
      shelter_id: filters.shelter_id,
    } as any;
    setParams(next);
    setIsFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilters({});
    setSelectedSpecies(undefined);
    // Clear from applied params
    setParams((prev) => ({
      ...prev,
      page: 1,
      species_id: undefined,
      // @ts-ignore
      sex_id: undefined,
      size_id: undefined,
      status_id: undefined,
      color_id: undefined,
      breed_id: undefined,
      temperament_id: undefined,
      shelter_id: undefined,
    }));
  };

  const removeFilter = (key: keyof typeof filters) => {
    setFilters((f) => ({ ...f, [key]: undefined, ...(key === 'species_id' ? { breed_id: undefined } : {}) }));
    setParams((prev: any) => ({
      ...prev,
      page: 1,
      [key]: undefined,
      ...(key === 'species_id' ? { breed_id: undefined } : {}),
    }));
    if (key === 'species_id') setSelectedSpecies(undefined);
  };

  const labelFor = (opts: {id:number;label:string}[], id?: number) => {
    if (id == null) return undefined;
    return opts.find(o=>o.id===id)?.label;
  };

  const adopterDisplayName = (animal: any) => {
    const adoptionInfo = (animal as any)?.adoption;
    const profile =
      adoptionInfo?.adopterProfile ||
      adoptionInfo?.application?.adopterProfile ||
      adoptionInfo?.adopter ||
      null;
    const user = profile?.user || adoptionInfo?.adopter || null;
    const fullName =
      user?.fullName ||
      user?.full_name ||
      user?.name ||
      user?.nome ||
      profile?.fullName ||
      profile?.full_name ||
      profile?.name ||
      profile?.nome ||
      animal?.adopterName ||
      animal?.adopter_name ||
      '';
    const firstLast = [
      profile?.firstName,
      profile?.lastName,
      profile?.first_name,
      profile?.last_name,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
    return fullName || firstLast || user?.email || profile?.email || animal?.adopterEmail || animal?.adopter_email || '';
  };

  const adoptionDateOf = (animal: any) =>
    (animal as any)?.adoption?.date ||
    (animal as any)?.adoption?.adoptedAt ||
    (animal as any)?.adoptedAt ||
    (animal as any)?.updatedAt ||
    (animal as any)?.createdAt;

  const animalStatusLabel = (animal: any) =>
    animal?.status_label || animal?.statusLabel || animal?.status?.label || animal?.status?.name || '-';

  const renderAnswers = () => {
    const answers = viewFormResponse?.answersJson || viewFormResponse?.answers;
    if (!answers) {
      return <div className="text-sm text-muted-foreground">No hay respuestas disponibles.</div>;
    }

    const templateSections: any[] = viewFormTemplate?.schemaJson?.sections || [];

    const lookupLabelFor = (field: any, raw: any) => {
      if (raw === undefined || raw === null || raw === '') return undefined;
      if (typeof raw === 'object') {
        const lbl = (raw as any).label || (raw as any).name || (raw as any).city || (raw as any).title;
        if (lbl) return String(lbl);
      }
      const option = (field.options || []).find((opt: any) => String(opt.value) === String(raw));
      if (option) return option.label || option.value;
      const numeric = Number(raw);
      const findIn = (collection?: any[]) =>
        collection?.find((i: any) => Number(i.id) === numeric || Number(i.value) === numeric || String(i.code) === String(raw));
      const fromLookupType = () => {
        switch (field.lookupType) {
          case 'species': return findIn(species);
          case 'sex': return findIn(sexes);
          case 'size': return findIn(sizes);
          case 'breed': return findIn(breedsAll);
          case 'color': return findIn(colors);
          case 'status': return findIn(statuses);
          default: return undefined;
        }
      };
      const idMatchers: Record<string, any[]> = {
        preferred_species_id: species,
        species_id: species,
        speciesId: species,
        preferred_size_id: sizes,
        size_id: sizes,
        sizeId: sizes,
        preferred_sex_id: sexes,
        sex_id: sexes,
        sexId: sexes,
        preferred_breed_id: breedsAll,
        breed_id: breedsAll,
        breedId: breedsAll,
        preferred_color_id: colors,
        color_id: colors,
        colorId: colors,
        city: (snapshot as any)?.cities,
        city_id: (snapshot as any)?.cities,
        cityId: (snapshot as any)?.cities,
      };
      const collection = idMatchers[field.id] || idMatchers[field.binding?.column || ''];
      const found = fromLookupType() || findIn(collection);
      return found ? toOptionLabel(found as any) : undefined;
    };

    const renderValue = (value: any, field?: any) => {
      if (Array.isArray(value)) return value.map((v) => renderValue(v, field)).join(', ');
      if (typeof value === 'object' && value !== null) {
        const objLabel =
          (value as any).label ||
          (value as any).name ||
          (value as any).city ||
          (value as any).title;
        if (objLabel) return String(objLabel);
      }
      if (value === true || value === 'true') return 'Sí';
      if (value === false || value === 'false') return 'No';
      if (value === undefined || value === null || value === '') return '—';
      if (field) {
        const label = lookupLabelFor(field, value);
        if (label) return label;
      }
      return String(value);
    };

    if (templateSections.length) {
      return (
        <div className="space-y-4">
          {templateSections.map((section) => (
            <div key={section.id} className="border rounded-lg p-3">
              <h5 className="font-semibold mb-2 text-sm">{section.label || section.title || section.id}</h5>
              <div className="grid gap-3 sm:grid-cols-2">
                {(section.fields || []).map((field: any) => (
                  <div key={field.id} className="text-sm">
                    <p className="text-muted-foreground">{field.label || field.title || field.id}</p>
                    <p className="font-medium">{renderValue(answers[field.id], field)}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(answers).map(([key, value]) => (
          <div key={key} className="text-sm">
            <p className="text-muted-foreground">{key}</p>
            <p className="font-medium">{renderValue(value)}</p>
          </div>
        ))}
      </div>
    );
  };

  const openView = async (animal: any) => {
    setIsViewOpen(true);
    setIsViewLoading(true);
    setViewAnimal(null);
    setViewApplication(null);
    setViewFormResponse(null);
    setViewFormTemplate(null);
    setViewTimeline([]);
    try {
      const fullAnimal = await animalsApi.getById(Number(animal.id));
      setViewAnimal(fullAnimal);
      const adoptionInfo = (fullAnimal as any)?.adoption;
      let application = adoptionInfo?.application || null;
      if (!application) {
        const filter = {
          order: ['createdAt DESC'],
          include: [
            {
              relation: 'adopterProfile',
              scope: {
                fields: ['id', 'firstName', 'lastName', 'first_name', 'last_name', 'fullName', 'full_name', 'name', 'email', 'phone'],
                include: [{ relation: 'user', scope: { fields: ['id', 'fullName', 'email', 'phone'] } }],
              },
            },
          ],
        };
        const apps = await adoptionApi.listApplications({ animalId: Number(animal.id), filter });
        application = Array.isArray(apps) ? apps[0] : (apps as any)?.data?.[0];
      }
      setViewApplication(application);
      const appId = application?.id || adoptionInfo?.applicationId || adoptionInfo?.application?.id;
      if (appId) {
        const [fullApp, tl] = await Promise.all([
          adoptionApi.getApplicationById(Number(appId)),
          adoptionApi.getApplicationTimeline(Number(appId)).catch(() => []),
        ]);
        setViewApplication((prev: any) => ({ ...(prev || {}), ...(fullApp as any) }));
        const response =
          (fullApp as any).formResponse ||
          (fullApp as any).adoptionFormResponse ||
          (fullApp as any).form_response ||
          null;
        const template =
          (fullApp as any).adoptionFormTemplate ||
          (fullApp as any).formTemplate ||
          null;
        setViewFormResponse(response);
        setViewFormTemplate(template);
        setViewTimeline(Array.isArray(tl) ? tl : []);
      }
    } catch {
      toast({ title: 'No se pudieron cargar los detalles', variant: 'destructive' });
    } finally {
      setIsViewLoading(false);
    }
  };

  function Combobox({
    value,
    onChange,
    options,
    placeholder,
    disabled,
  }: { value?: number; onChange: (v?: number)=>void; options: {id:number;label:string}[]; placeholder?: string; disabled?: boolean }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const current = options.find(o=>o.id===value);
    const filtered = useMemo(()=>{
      const q = query.trim().toLowerCase();
      if (!q) return options.slice(0,100);
      return options.filter(o=>o.label.toLowerCase().includes(q)).slice(0,100);
    },[options,query]);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between" disabled={disabled}>
            {current ? current.label : (placeholder || 'Selecciona...')}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder="Buscar..." value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {filtered.map(o=> (
                  <CommandItem key={o.id} value={o.label} onSelect={()=>{ onChange(o.id); setOpen(false); }}>
                    <Check className={"mr-2 h-4 w-4 "+(o.id===value?'opacity-100':'opacity-0')} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  // Inline status editor for rows
  function RowStatusEditor({
    animalId,
    currentId,
    currentLabel,
    currentCode,
  }: {
    animalId: number;
    currentId?: number;
    currentLabel?: string;
    currentCode?: string | number | null;
  }) {
    const [open, setOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const opts = useMemo(() => (statuses || []).map((i:any)=>({ id: i.id, label: toOptionLabel(i), code: i.code })), [statuses]);
    const current = useMemo(() => {
      if (currentId != null) return opts.find(o => Number(o.id) === Number(currentId));
      return opts.find(o => o.label === currentLabel);
    }, [opts, currentId, currentLabel]);
    const currentType = statusTypeOf(current?.code ?? currentCode, current?.label ?? currentLabel);
    const allowedTypes = useMemo(() => {
      if (currentType === 'available') return new Set<AnimalStatusType>(['paused', 'deceased']);
      if (currentType === 'paused') return new Set<AnimalStatusType>(['available']);
      return null;
    }, [currentType]);
    const allowedOpts = useMemo(() => {
      if (!allowedTypes) return [];
      return opts.filter((o) => allowedTypes.has(statusTypeOf(o.code, o.label)));
    }, [opts, allowedTypes]);

    const badgeClass = (code?: string, label?: string) => {
      const norm = normalizeStatusText(code, label);
      // Disponível
      if (/dispon/i.test(norm) || /available/i.test(norm)) return 'bg-green-100 text-green-800';
      // Adotado
      if (isAdoptedStatus(code, label)) return 'bg-blue-100 text-blue-800';
      // Match explicit process codes first
      if (/proc/i.test(norm) || /\bin_adoption_process\b/.test(norm) || /\badoption_process\b/.test(norm)) return 'bg-orange-100 text-orange-800';
      // Em processo de adoção
      if (/(em\s*)?process/.test(norm) || /proceso/.test(norm)) return 'bg-orange-100 text-orange-800';
      // Falecido
      if (/falecid/i.test(norm) || /fallecid/i.test(norm) || /deceas|muert/.test(norm)) return 'bg-gray-200 text-gray-800';
      // Pausado
      if (/pausad/i.test(norm) || /paused|suspendid/.test(norm)) return 'bg-red-100 text-red-800';
      // Default
      return 'bg-blue-100 text-blue-800';
    };

    const onSelect = async (newId: number) => {
      if (allowedTypes && !allowedOpts.some((opt) => Number(opt.id) === Number(newId))) return;
      try {
        setIsUpdating(true);
        await animalsApi.update(animalId, { statusId: newId });
        toast({ title: 'Estado actualizado' });
        setOpen(false);
        // Refresh list
        await queryClient.invalidateQueries({ queryKey: ['animals-search'] });
      } catch (e: any) {
        toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'No se pudo actualizar el estado' });
      } finally {
        setIsUpdating(false);
      }
    };

    const currentClasses = badgeClass(current?.code, current?.label || currentLabel);

    // Read-only for customers or for restricted transitions
    if (isCustomer || !allowedTypes || allowedOpts.length === 0) {
      const classes = badgeClass(current?.code, current?.label || currentLabel);
      return <span className={`px-2 py-1 rounded-full text-xs ${classes}`}>{current?.label || currentLabel || '-'}</span>;
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`px-2 py-1 rounded-full text-xs ${currentClasses} inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-ring min-w-[180px] justify-between`}
          >
            {isUpdating && <Loader2 className="h-3 w-3 animate-spin" />}
            <span>{current?.label || currentLabel || '-'}</span>
            <ChevronsUpDown className="h-3 w-3 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0">
          <Command>
            <CommandList>
              <CommandEmpty>Sin resultados</CommandEmpty>
              <CommandGroup>
                {allowedOpts.map(o => {
                  const optionClasses = badgeClass(o.code, o.label);
                  return (
                    <CommandItem key={o.id} value={o.label} onSelect={() => onSelect(Number(o.id))}>
                      <Check className={"mr-2 h-4 w-4 "+(o.id===current?.id? 'opacity-100':'opacity-0')} />
                      <span className={`px-2 py-0.5 rounded-full text-xs ${optionClasses}`}>{o.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  const rawRows: any[] = (data?.data as any[]) || [];
  const rows = useMemo(() => {
    // First, exclude soft-deleted items (workaround for backend issue)
    const activeAnimals = rawRows.filter((animal: any) => !animal.deleted_at && !animal.deletedAt);

    if (!adoptedStatusIds.length) return activeAnimals;

    return activeAnimals.filter((animal: any) => {
      const statusCode = (animal as any)?.statusCode ?? (animal as any)?.status?.code;
      const statusLabel = animal?.statusLabel ?? (animal as any)?.status?.label;
      const adopted = isAdoptedStatus(statusCode, statusLabel);
      return activeTab === 'adopted' ? adopted : !adopted;
    });
  }, [rawRows, adoptedStatusIds, activeTab]);
  const total: number = (data as any)?.total ?? rawRows.length;
  const pageSize = params.pageSize || 20;
  const page = params.page || 1;

  const applySort = (column: typeof sort) => {
    const newDir: 'asc'|'desc' = sort === column ? (dir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSort(column);
    setDir(newDir);
  };

  const toAbsolutePhotoUrl = (u?: string) => {
    if (!u) return undefined;
    if (/^https?:\/\//i.test(u)) return u;
    const base = env.apiUrl.replace(/\/$/, '');
    const path = u.replace(/^\//, '');
    return `${base}/${path}`;
  };

  const formatDate = (value?: string | number | Date) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  };

  // Photo preview modal
  const [isPhotoOpen, setIsPhotoOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<{ url: string; title?: string } | null>(null);
  const openPhoto = (url?: string, title?: string) => {
    const abs = toAbsolutePhotoUrl(url);
    if (!abs) return;
    setPhotoPreview({ url: abs, title });
    setIsPhotoOpen(true);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">{viewMode === 'adopted' ? 'Animales Adoptados' : 'Animales Disponibles'}</h1>
            <p className="text-muted-foreground">
              {viewMode === 'adopted' ? 'Historial de animales adoptados' : 'Gestiona los animales disponibles para adopción'}
            </p>
          </div>
          {!isCustomer && (
          <Button asChild>
            <Link to="/animals/new">
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Animal
            </Link>
          </Button>
          )}
        </div>

        <Card className="p-6 mb-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nombre o microchip..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch}>
              <Search className="mr-2 h-4 w-4" />
              Buscar
            </Button>
            <Button variant="outline" onClick={()=> setIsFiltersOpen(true)}>
              <Filter className="mr-2 h-4 w-4" />
              Filtros
            </Button>
          </div>
        </Card>


        {/* Photo preview dialog */}
        <Dialog open={isPhotoOpen} onOpenChange={setIsPhotoOpen}>
          <DialogContent className="max-w-[95vw]">
            {photoPreview?.title && (
              <DialogHeader>
                <DialogTitle>{photoPreview.title}</DialogTitle>
              </DialogHeader>
            )}
            <div className="w-full h-full flex items-center justify-center">
              {photoPreview?.url && (
                // eslint-disable-next-line jsx-a11y/alt-text
                <img
                  src={photoPreview.url}
                  className="max-w-[90vw] max-h-[80vh] object-contain rounded"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalle de adopción</DialogTitle>
            </DialogHeader>
            {isViewLoading ? (
              <div className="py-8 text-center text-muted-foreground">Cargando detalles…</div>
            ) : viewAnimal ? (
              <div className="space-y-6">
                <div className="border rounded-lg p-3">
                  <h3 className="text-lg font-semibold mb-3">Animal</h3>
                  {Array.isArray(viewAnimal.photos) && viewAnimal.photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {viewAnimal.photos.map((p: any) => {
                        const url = toAbsolutePhotoUrl(p.url || p.photo || p.primaryPhotoUrl || p.path);
                        if (!url) return null;
                        return (
                          <button
                            key={p.id || url}
                            type="button"
                            onClick={() => openPhoto(url, viewAnimal.name || 'Foto del animal')}
                            className="focus:outline-none focus:ring-2 focus:ring-ring rounded"
                            aria-label="Ver foto"
                          >
                            <PhotoThumb src={url} alt="Foto del animal" className="h-14 w-14 rounded" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="text-sm">
                      <p className="text-muted-foreground">Nombre</p>
                      <p className="font-medium">{viewAnimal.name || '-'}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Especie</p>
                      <p className="font-medium">
                        {viewAnimal.species_label ||
                          viewAnimal.speciesLabel ||
                          labelFor(speciesOpts, Number(viewAnimal.species_id ?? viewAnimal.speciesId)) ||
                          '-'}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Sexo</p>
                      <p className="font-medium">
                        {viewAnimal.sex_label ||
                          viewAnimal.sexLabel ||
                          labelFor(sexOpts, Number(viewAnimal.sex_id ?? viewAnimal.sexId)) ||
                          '-'}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Tamaño</p>
                      <p className="font-medium">
                        {viewAnimal.size_label ||
                          viewAnimal.sizeLabel ||
                          labelFor(sizeOpts, Number(viewAnimal.size_id ?? viewAnimal.sizeId)) ||
                          '-'}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Raza</p>
                      <p className="font-medium">
                        {viewAnimal.breed_label ||
                          viewAnimal.breedLabel ||
                          labelFor((breedsAll || []).map((i: any) => ({ id: i.id, label: toOptionLabel(i) })), Number(viewAnimal.breed_id ?? viewAnimal.breedId)) ||
                          '-'}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Color</p>
                      <p className="font-medium">
                        {viewAnimal.color_label ||
                          viewAnimal.colorLabel ||
                          labelFor(colorOpts, Number(viewAnimal.color_id ?? viewAnimal.colorId)) ||
                          '-'}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Microchip</p>
                      <p className="font-medium">{viewAnimal.microchip || '-'}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Ubicación</p>
                      <p className="font-medium">{viewAnimal.location || '-'}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Estado</p>
                      <p className="font-medium">
                        {viewAnimal.status_label ||
                          viewAnimal.statusLabel ||
                          labelFor(statusOpts, Number(viewAnimal.status_id ?? viewAnimal.statusId)) ||
                          '-'}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Entidad</p>
                      <p className="font-medium">{viewAnimal.shelter?.name || viewAnimal.shelterName || '-'}</p>
                    </div>
                    <div className="text-sm sm:col-span-2">
                      <p className="text-muted-foreground">Historia</p>
                      <p className="font-medium whitespace-pre-line">{viewAnimal.behavior || '-'}</p>
                    </div>
                    <div className="text-sm sm:col-span-2">
                      <p className="text-muted-foreground">Necesidades</p>
                      <p className="font-medium whitespace-pre-line">
                        {viewAnimal.animal_needs ?? viewAnimal.animalNeeds ?? '-'}
                      </p>
                    </div>
                    <div className="text-sm sm:col-span-2">
                      <p className="text-muted-foreground">Términos de aceptación</p>
                      <p className="font-medium whitespace-pre-line">
                        {viewAnimal.acceptance_terms ?? viewAnimal.acceptanceTerms ?? '-'}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="text-muted-foreground">Fecha de adopción</p>
                      <p className="font-medium">{formatDate(adoptionDateOf(viewAnimal))}</p>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-3">
                  <h3 className="text-lg font-semibold mb-3">Adotante</h3>
                  {(() => {
                    const adoptionInfo = (viewAnimal as any)?.adoption;
                    const profile =
                      (viewApplication as any)?.adopterProfile ||
                      adoptionInfo?.adopterProfile ||
                      adoptionInfo?.adopter ||
                      null;
                    const user = profile?.user || adoptionInfo?.adopter || null;
                    const cityValue = profile?.city ?? profile?.cityId ?? profile?.city_id;
                    const cityLabel = (() => {
                      if (!cityValue) return undefined;
                      if (typeof cityValue === 'object') {
                        return cityValue.label || cityValue.name || cityValue.city;
                      }
                      const id = Number(cityValue);
                      if (Number.isNaN(id)) return String(cityValue);
                      const cities = (snapshot as any)?.cities || [];
                      const found = cities.find((c: any) => Number(c.id) === id);
                      return found ? toOptionLabel(found) : String(cityValue);
                    })();
                    const name =
                      user?.fullName ||
                      user?.full_name ||
                      user?.name ||
                      user?.nome ||
                      profile?.fullName ||
                      profile?.full_name ||
                      profile?.name ||
                      profile?.nome ||
                      [profile?.firstName, profile?.lastName, profile?.first_name, profile?.last_name]
                        .filter(Boolean)
                        .join(' ')
                        .trim() ||
                      '-';
                    const approverFromApp =
                      (viewApplication as any)?.approvedByUser ||
                      (viewApplication as any)?.approvedBy ||
                      (viewApplication as any)?.approved_by;
                    const approverFromTimeline = (viewTimeline || []).find((e: any) => {
                      const desc = String(e?.description || '').toLowerCase();
                      return e?.eventType === 'STATUS_CHANGED' && /aprob|approv/.test(desc);
                    });
                    const approverName =
                      approverFromApp?.fullName ||
                      approverFromApp?.full_name ||
                      approverFromApp?.name ||
                      approverFromApp?.email ||
                      approverFromApp?.id ||
                      approverFromTimeline?.createdByUser?.fullName ||
                      approverFromTimeline?.createdByUser?.full_name ||
                      approverFromTimeline?.createdByUser?.email ||
                      approverFromTimeline?.createdBy ||
                      '-';
                    return (
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="text-sm">
                          <p className="text-muted-foreground">Nombre</p>
                          <p className="font-medium">{name}</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-muted-foreground">Email</p>
                          <p className="font-medium">{user?.email || profile?.email || '-'}</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-muted-foreground">Teléfono</p>
                          <p className="font-medium">{user?.phone || profile?.phone || '-'}</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-muted-foreground">Dirección</p>
                          <p className="font-medium">{profile?.address || '-'}</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-muted-foreground">Ciudad</p>
                          <p className="font-medium">{cityLabel || profile?.cityName || profile?.cityLabel || '-'}</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-muted-foreground">Ocupación</p>
                          <p className="font-medium">{profile?.occupation || '-'}</p>
                        </div>
                        <div className="text-sm sm:col-span-2">
                          <p className="text-muted-foreground">Motivación</p>
                          <p className="font-medium whitespace-pre-line">{profile?.motivation || '-'}</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-muted-foreground">Aprobado por</p>
                          <p className="font-medium">{approverName}</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Formulario de adopción</h3>
                  {renderAnswers()}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">No hay detalles disponibles.</div>
            )}
          </DialogContent>
        </Dialog>

        {/* Active Filters Chips */}
        {(
          params.species_id || (params as any).sex_id || params.size_id || (params as any).temperament_id || params.status_id || params.color_id || params.breed_id || params.shelter_id
        ) && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {params.species_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('species_id')} aria-label="Quitar especie">
                Especie: {labelFor(speciesOpts, params.species_id)} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            {(params as any).sex_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('sex_id')} aria-label="Quitar sexo">
                Sexo: {labelFor(sexOpts, (params as any).sex_id)} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            {params.size_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('size_id')} aria-label="Quitar tamaño">
                Tamaño: {labelFor(sizeOpts, params.size_id)} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            {(params as any).temperament_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('temperament_id')} aria-label="Quitar temperamento">
                Temperamento: {labelFor(temperamentOpts, (params as any).temperament_id)} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            {params.status_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('status_id')} aria-label="Quitar estado">
                Estado: {labelFor(statusOpts, params.status_id)} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            {params.color_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('color_id')} aria-label="Quitar color">
                Color: {labelFor(colorOpts, params.color_id)} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            {params.breed_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('breed_id')} aria-label="Quitar raza">
                Raza: {labelFor(
                  (breedsAll || []).map((i:any)=>({id:i.id,label:toOptionLabel(i)})),
                  params.breed_id
                )} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            {params.shelter_id && (
              <Button variant="outline" size="sm" onClick={() => removeFilter('shelter_id')} aria-label="Quitar entidad">
                Entidad: {labelFor(shelterOpts, params.shelter_id)} <X className="ml-2 h-3 w-3" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Reset filtros
            </Button>
          </div>
        )}

        {/* Filters Dialog */}
        <Dialog open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Filtros</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div>
                <label className="text-sm mb-1 block">Especie</label>
                <Combobox
                  value={filters.species_id}
                  onChange={(v)=>{ setFilters({...filters, species_id: v, breed_id: undefined}); setSelectedSpecies(v); }}
                  options={speciesOpts}
                  placeholder="Todas"
                />
              </div>
              <div>
                <label className="text-sm mb-1 block">Sexo</label>
                <Combobox
                  value={filters.sex_id}
                  onChange={(v)=> setFilters({...filters, sex_id: v})}
                  options={sexOpts}
                  placeholder="Todos"
                />
              </div>
              <div>
                <label className="text-sm mb-1 block">Tamaño</label>
                <Combobox
                  value={filters.size_id}
                  onChange={(v)=> setFilters({...filters, size_id: v})}
                  options={sizeOpts}
                  placeholder="Todos"
                />
              </div>
              <div>
                <label className="text-sm mb-1 block">Temperamento</label>
                <Combobox
                  value={filters.temperament_id}
                  onChange={(v)=> setFilters({...filters, temperament_id: v})}
                  options={temperamentOpts}
                  placeholder="Todos"
                />
              </div>
              <div>
                <label className="text-sm mb-1 block">Estado</label>
                <Combobox
                  value={filters.status_id}
                  onChange={(v)=> setFilters({...filters, status_id: v})}
                  options={statusFilterOptions}
                  placeholder="Todos"
                  disabled={!statusFilterOptions.length}
                />
              </div>
              <div>
                <label className="text-sm mb-1 block">Color</label>
                <Combobox
                  value={filters.color_id}
                  onChange={(v)=> setFilters({...filters, color_id: v})}
                  options={colorOpts}
                  placeholder="Todos"
                />
              </div>
              <div>
                <label className="text-sm mb-1 block">Raza</label>
                <Combobox
                  value={filters.breed_id}
                  onChange={(v)=> setFilters({...filters, breed_id: v})}
                  options={breedOpts}
                  placeholder={selectedSpecies? 'Selecciona':'Todas'}
                  disabled={!selectedSpecies}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm mb-1 block">Entidad</label>
                <Combobox
                  value={filters.shelter_id}
                  onChange={(v)=> setFilters({...filters, shelter_id: v})}
                  options={shelterOpts}
                  placeholder="Todos"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={clearFilters}>Limpiar</Button>
              <Button onClick={applyFilters}>Aplicar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                {activeTab === 'adopted' ? (
                  <>
                    <TableHead className="cursor-pointer select-none">ID</TableHead>
                    <TableHead className="cursor-pointer select-none">Animal</TableHead>
                    <TableHead className="cursor-pointer select-none">Entidad</TableHead>
                    <TableHead className="cursor-pointer select-none">Adoptante</TableHead>
                    <TableHead className="cursor-pointer select-none">Fecha</TableHead>
                    <TableHead className="cursor-pointer select-none">Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead>Foto</TableHead>
                    <TableHead onClick={()=>applySort('name')} className="cursor-pointer select-none">
                      Nombre {sort==='name' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead onClick={()=>applySort('species')} className="cursor-pointer select-none">
                      Especie {sort==='species' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead onClick={()=>applySort('sex')} className="cursor-pointer select-none">
                      Sexo {sort==='sex' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead onClick={()=>applySort('size')} className="cursor-pointer select-none">
                      Tamaño {sort==='size' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead onClick={()=>applySort('breed')} className="cursor-pointer select-none">
                      Raza {sort==='breed' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead onClick={()=>applySort('status')} className="cursor-pointer select-none">
                      Estado {sort==='status' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead onClick={()=>applySort('shelter')} className="cursor-pointer select-none">
                      Entidad {sort==='shelter' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead onClick={()=>applySort('microchip')} className="cursor-pointer select-none">
                      Microchip {sort==='microchip' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead onClick={()=>applySort('createdAt')} className="cursor-pointer select-none">
                      Creado {sort==='createdAt' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />):<ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                    </TableHead>
                    <TableHead>Acciones</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                    No se encontraron animales
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((animal: any) => (
                  <TableRow key={animal.id}>
                    {activeTab === 'adopted' ? (
                      <>
                        <TableCell className="font-medium">#{animal.id}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {animal.primaryPhotoUrl ? (
                              <PhotoThumb
                                src={toAbsolutePhotoUrl(animal.primaryPhotoUrl)}
                                alt={animal.name || 'Animal'}
                                className="w-10 h-10 rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                                <PawPrint className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                            <div>
                              <div className="font-semibold">{animal.name || `Animal #${animal.id}`}</div>
                              <div className="text-xs text-muted-foreground">{animal.speciesLabel || ''}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{animal.shelterName || animal.shelter?.name || '—'}</TableCell>
                        <TableCell>
                          <div className="text-sm">{adopterDisplayName(animal) || '—'}</div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <CalendarDays className="h-4 w-4" />
                            {formatDate(adoptionDateOf(animal))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={isAdoptedStatus(animal?.statusCode ?? animal?.status?.code, animalStatusLabel(animal)) ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-800'}>
                            {animalStatusLabel(animal)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" onClick={() => openView(animal)}>
                            Ver detalle
                          </Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          {animal.primaryPhotoUrl ? (
                            <button
                              type="button"
                              onClick={() => openPhoto(animal.primaryPhotoUrl, animal.name)}
                              className="focus:outline-none focus:ring-2 focus:ring-ring rounded"
                              aria-label="Ver foto"
                            >
                              <PhotoThumb
                                src={toAbsolutePhotoUrl(animal.primaryPhotoUrl)}
                                alt="Foto"
                                className="h-10 w-10 rounded"
                              />
                            </button>
                          ) : (
                            <div className="h-10 w-10 rounded bg-muted" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{animal.name || '-'}</TableCell>
                        <TableCell>{animal.speciesLabel || '-'}</TableCell>
                        <TableCell>{animal.sexLabel || '-'}</TableCell>
                        <TableCell>{animal.sizeLabel || '-'}</TableCell>
                        <TableCell>{animal.breedLabel || '-'}</TableCell>
                        <TableCell>
                          <RowStatusEditor
                            animalId={animal.id}
                            currentId={(animal as any).statusId ?? (animal as any).status_id}
                            currentCode={(animal as any).statusCode ?? (animal as any)?.status?.code}
                            currentLabel={animal.statusLabel}
                          />
                        </TableCell>
                        <TableCell>{animal.shelterName || '-'}</TableCell>
                        <TableCell className="text-xs">{animal.location || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{animal.microchip || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDate(animal.createdAt)}</TableCell>
                        {!isCustomer ? (
                          <TableCell className="space-x-1">
                            <Button variant="ghost" size="icon" asChild aria-label="Editar" title="Editar">
                              <Link to={`/animals/${animal.id}/edit`}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                          </TableCell>
                        ) : (
                          <TableCell />
                        )}
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="p-4 border-t flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Mostrando {rows.length} de {total} resultados
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setParams({ ...params, page: page - 1 })}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page * pageSize >= total}
                onClick={() => setParams({ ...params, page: page + 1 })}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
